import { EditableText, defineBlock } from "eddev/blocks"
import { cn, tv, VariantProps } from "@utils/tw"
import { ComponentPropsWithoutRef, forwardRef } from "react"
import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Heading } from "@components/atoms/Heading"
import { Link } from "eddev/routing"
import { twMerge } from "tailwind-merge"

export const meta: BlockMeta = {
  title: "Card dashboard",
  category: "common",
  icon: "feedback",
  keywords: ["cards"],
  tags: ["root"],
  editMode: "preview",
  lazyLoad: false,
  conditions: {
    postTypes: ["page", "post"],
  },
}

export default defineBlock("general/card-dashboard", (props) => {
  return (
    <div
      className={twMerge(
        "rounded-md my-6 pt-6 px-5 pb-5 mx-auto",
        "w-[var(--tw-grid-inner-width)]",
        "[&_h2]:m-0 [&_h2]:type-title-m [&_h2]:text-center [&_h2]:w-full [&_h2]:block",
        "[&_*>p]:m-0 [&_*>p]:leading-[150%] [&_*>p]:text-left",
        "[&_a]:!no-underline",
        "[&>div]:w-full",
        props.colourScheme === "light" ? "bg-light-grey text-black [&_a>div]:bg-yellow" : "bg-black text-white",
      )}
    >
      <div className="grid-auto gap-6">
        <div className="col-span-12 lg:col-span-10 lg:col-start-2">
          <EditableText as="h2" store="title" placeholder="Enter title..." className="w-full !text-center" />
        </div>

        {props.image || props.video ? (
          <>
            <div className="col-span-12 md:col-span-6 flex flex-col">
              <MediaContainer>
                {props.video?.mediaItemUrl ? (
                  <video autoPlay muted loop playsInline>
                    <source src={props.video.mediaItemUrl} type="video/mp4" />
                  </video>
                ) : (
                  <>{props.image && <ResponsiveImage {...props.image} aspect={6 / 4} objectFit="cover" />}</>
                )}
              </MediaContainer>
            </div>
            <div className="col-span-12 md:col-span-6  flex flex-col gap-5 md:[&_a]:grow md:[&_a>div]:h-full md:[&_a>div>div]:p-5">
              <div className="flex flex-col gap-4 items-center [text-wrap:pretty]">
                <EditableText as="p" store="content" placeholder="Enter content..." />
              </div>
              {props.link1 && (
                <Link href={props.link1.link?.url ?? "#"} target={props.link1.link?.target ? "_blank" : undefined}>
                  <div className="w-full flex rounded-md gap-4 relative overflow-hidden transition-[background-color] ease-movement duration-300 [&_img]:transition-transform [&_img]:ease-movement [&_img]:duration-300 [&_video]:transition-transform [&_video]:ease-movement [&_video]:duration-300 h-[112px] text-white bg-light-grey [&_label]:type-label-caps lg:h-[125px] aaa-animate hover:-translate-y-[3px] hover:shadow-[0px_6px_12px_0px_rgba(34,34,34,0.08),0px_1px_3px_0px_rgba(34,34,34,0.1)]">
                    <ContentContainer dark>
                      <Heading as="label">{props.link1.subtitle}</Heading>

                      <Heading as="h4" className="type-title-s py-1">
                        {props.link1.link?.title ?? ""}
                      </Heading>
                    </ContentContainer>
                  </div>
                </Link>
              )}
              {props.link2 && (
                <Link href={props.link2.link?.url ?? "#"} target={props.link2.link?.target ? "_blank" : undefined}>
                  <div className="w-full flex rounded-md gap-4 relative overflow-hidden transition-[background-color] ease-movement duration-300 [&_img]:transition-transform [&_img]:ease-movement [&_img]:duration-300 [&_video]:transition-transform [&_video]:ease-movement [&_video]:duration-300 h-[112px] text-white bg-light-grey [&_label]:type-label-caps lg:h-[125px] aaa-animate hover:-translate-y-[3px] hover:shadow-[0px_6px_12px_0px_rgba(34,34,34,0.08),0px_1px_3px_0px_rgba(34,34,34,0.1)]">
                    <ContentContainer dark>
                      <Heading as="label">{props.link2.subtitle}</Heading>

                      <Heading as="h4" className="type-title-s py-1">
                        {props.link2.link?.title ?? ""}
                      </Heading>
                    </ContentContainer>
                  </div>
                </Link>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="col-span-12 md:col-span-6">
              {props.link1 && (
                <Link href={props.link1.link?.url ?? "#"} target={props.link1.link?.target ? "_blank" : undefined}>
                  <div className="w-full flex rounded-md gap-4 relative overflow-hidden transition-[background-color] ease-movement duration-300 [&_img]:transition-transform [&_img]:ease-movement [&_img]:duration-300 [&_video]:transition-transform [&_video]:ease-movement [&_video]:duration-300 h-[112px] text-white bg-light-grey [&_label]:type-label-caps lg:h-[125px] aaa-animate hover:-translate-y-[3px] hover:shadow-[0px_6px_12px_0px_rgba(34,34,34,0.08),0px_1px_3px_0px_rgba(34,34,34,0.1)]">
                    <ContentContainer>
                      <Heading as="label">{props.link1.subtitle}</Heading>

                      <Heading as="h4" className="type-title-s py-1">
                        {props.link1.link?.title ?? ""}
                      </Heading>
                    </ContentContainer>
                    {props.link1.image && (
                      <ImageContainer>
                        <ResponsiveImage {...props.link1.image} />
                      </ImageContainer>
                    )}
                  </div>
                </Link>
              )}
            </div>

            <div className="col-span-12 md:col-span-6">
              {props.link2 && (
                <Link href={props.link2.link?.url ?? "#"} target={props.link2.link?.target ? "_blank" : undefined}>
                  <div className="w-full flex rounded-md gap-4 relative overflow-hidden transition-[background-color] ease-movement duration-300 [&_img]:transition-transform [&_img]:ease-movement [&_img]:duration-300 [&_video]:transition-transform [&_video]:ease-movement [&_video]:duration-300 h-[112px] text-white bg-light-grey [&_label]:type-label-caps lg:h-[125px] aaa-animate hover:-translate-y-[3px] hover:shadow-[0px_6px_12px_0px_rgba(34,34,34,0.08),0px_1px_3px_0px_rgba(34,34,34,0.1)]">
                    <ContentContainer>
                      <Heading as="label">{props.link2.subtitle}</Heading>

                      <Heading as="h4" className="type-title-s py-1">
                        {props.link2.link?.title ?? ""}
                      </Heading>
                    </ContentContainer>
                    {props.link2.image && (
                      <ImageContainer>
                        <ResponsiveImage {...props.link2.image} />
                      </ImageContainer>
                    )}
                  </div>
                </Link>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
})

const contentContainerStyles = tv({
  base: [
    "flex flex-col gap-1 pointer-events-none z-[1] relative p-7 self-stretch justify-start",
    "[&_h3]:type-title-m [&_h3]:my-0",
    "lg:p-5",
  ],
  variants: {
    dark: {
      true: "text-black",
    },
  },
})

const ContentContainer = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<"div"> & VariantProps<typeof contentContainerStyles>
>(({ dark, className, ...props }, ref) => (
  <div ref={ref} className={contentContainerStyles({ dark, className })} {...props} />
))
ContentContainer.displayName = "ContentContainer"

const ImageContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "absolute top-0 left-0 w-full h-full z-0",
      "[&_div]:before:content-[''] [&_div]:before:absolute [&_div]:before:inset-0 [&_div]:before:bg-black [&_div]:before:opacity-50 [&_div]:before:z-[1]",
      className,
    )}
    {...props}
  />
))
ImageContainer.displayName = "ImageContainer"

const MediaContainer = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex w-full pt-[calc(100%*(9/16))] h-full overflow-hidden rounded-md relative",
      "[&_video]:absolute [&>div]:absolute [&_video]:w-full [&>div]:w-full [&_video]:h-full [&>div]:h-full [&_video]:object-cover [&>div]:object-cover [&_video]:top-0 [&>div]:top-0",
      "md:mb-0 md:pt-0",
      className,
    )}
    {...props}
  />
))
MediaContainer.displayName = "MediaContainer"
